/**
 * ChatSidebar — conversation history list with "New Chat" button.
 */
import { Box, List, ListItemButton, ListItemText, IconButton, Typography, Divider, Tooltip } from '@mui/material';
import { Add, Delete } from '@mui/icons-material';
import { alpha, useTheme } from '@mui/material/styles';
import type { Conversation } from '../../services/chatService';

interface Props {
  conversations: Conversation[];
  activeId: string | null;
  onSelect: (id: string) => void;
  onNew: () => void;
  onArchive: (id: string) => void;
}

export const ChatSidebar = ({ conversations, activeId, onSelect, onNew, onArchive }: Props) => {
  const theme = useTheme();

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    const now = new Date();
    const diffDays = Math.floor((now.getTime() - d.getTime()) / 86400000);
    if (diffDays === 0) return d.toLocaleTimeString('en-CA', { hour: '2-digit', minute: '2-digit' });
    if (diffDays < 7) return d.toLocaleDateString('en-CA', { weekday: 'short' });
    return d.toLocaleDateString('en-CA', { month: 'short', day: 'numeric' });
  };

  return (
    <Box sx={{
      width: 260, minWidth: 260, flexShrink: 0, display: 'flex', flexDirection: 'column',
      height: '100%', minHeight: 0, borderRight: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
      backgroundColor: theme.palette.grey[50],
    }}>
      <Box sx={{ p: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.82rem' }}>
          Conversations
        </Typography>
        <Tooltip title="New Chat">
          <IconButton size="small" onClick={onNew} color="primary">
            <Add fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
      <Divider />
      <List disablePadding sx={{
        flex: 1, minHeight: 0, overflowY: 'auto', overscrollBehavior: 'contain',
        WebkitOverflowScrolling: 'touch', scrollBehavior: 'smooth',
        // Thin, unobtrusive scrollbar that only tints on hover (theme tokens only).
        scrollbarWidth: 'thin',
        scrollbarColor: `${alpha(theme.palette.text.primary, 0.2)} transparent`,
        '&::-webkit-scrollbar': { width: 6 },
        '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: 'transparent', borderRadius: 3,
          transition: 'background-color 0.2s ease',
        },
        '&:hover::-webkit-scrollbar-thumb': { backgroundColor: alpha(theme.palette.text.primary, 0.18) },
        '&::-webkit-scrollbar-thumb:hover': { backgroundColor: alpha(theme.palette.text.primary, 0.32) },
      }}>
        {conversations.length === 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ p: 2, display: 'block', textAlign: 'center' }}>
            No conversations yet
          </Typography>
        )}
        {conversations.map((c) => {
          const isActive = c.ConversationKey === activeId;
          return (
            <ListItemButton
              key={c.ConversationKey}
              selected={isActive}
              onClick={() => onSelect(c.ConversationKey)}
              sx={{
                py: 1, px: 1.5, alignItems: 'flex-start', position: 'relative',
                // Active accent bar on the left edge
                '&::before': {
                  content: '""', position: 'absolute', left: 0, top: 6, bottom: 6, width: 3,
                  borderRadius: '0 2px 2px 0',
                  backgroundColor: isActive ? theme.palette.primary.main : 'transparent',
                  transition: 'background-color 0.15s ease',
                },
                '&.Mui-selected': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.08),
                },
                // Archive action appears on hover only (keyboard focus keeps it visible)
                '& .conv-archive': { opacity: 0, transition: 'opacity 0.15s ease' },
                '&:hover .conv-archive, & .conv-archive:focus-visible': { opacity: 0.7 },
                '& .conv-archive:hover': { opacity: 1 },
              }}
            >
              <ListItemText
                primary={c.Title || 'New Chat'}
                secondary={`${c.MessageCount} msgs · ${formatDate(c.LastMessageAt || c.sysCreateDate)}`}
                primaryTypographyProps={{
                  fontSize: '0.78rem', fontWeight: isActive ? 600 : 400,
                  noWrap: true,
                }}
                secondaryTypographyProps={{ fontSize: '0.65rem' }}
              />
              <Tooltip title="Archive">
                <IconButton
                  size="small"
                  className="conv-archive"
                  onClick={(e) => { e.stopPropagation(); onArchive(c.ConversationKey); }}
                  sx={{ ml: 0.5 }}
                >
                  <Delete sx={{ fontSize: 14 }} />
                </IconButton>
              </Tooltip>
            </ListItemButton>
          );
        })}
      </List>
    </Box>
  );
};
